/* Custom SVG candlestick chart - sharp, terminal aesthetic */
export default function CandleChart({ candles = [], height = 320, signal = null }) {
  if (!candles.length) {
    return (
      <div className="flex items-center justify-center text-zinc-500 text-sm font-mono" style={{ height }}>
        No candle data
      </div>
    );
  }
  const padX = 10;
  const padY = 16;
  const W = 900;
  const H = height;
  const n = candles.length;
  const cw = (W - padX * 2) / n;
  const bodyW = Math.max(2, cw * 0.65);

  let hi = -Infinity, lo = Infinity;
  candles.forEach((c) => { hi = Math.max(hi, c.high); lo = Math.min(lo, c.low); });
  const span = (hi - lo) || 1;
  const range = span * 1.08;
  const mid = (hi + lo) / 2;
  const yMax = mid + range / 2;
  const yMin = mid - range / 2;

  const yOf = (v) => padY + ((yMax - v) / (yMax - yMin)) * (H - padY * 2);
  const xOf = (i) => padX + cw * i + cw / 2;

  const gridLines = 4;
  const gridYs = Array.from({ length: gridLines + 1 }, (_, i) =>
    yMin + ((yMax - yMin) * i) / gridLines
  );

  // Highlight last 3 candles (A, B, C pattern window)
  const aIdx = n - 3, bIdx = n - 2, cIdx = n - 1;

  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        {/* grid */}
        {gridYs.map((v, i) => (
          <line key={i} x1={padX} y1={yOf(v)} x2={W - padX} y2={yOf(v)}
                stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        ))}
        {gridYs.map((v, i) => (
          <text key={`l${i}`} x={W - padX} y={yOf(v) - 2} fontSize="10" textAnchor="end"
                fill="#52525B" fontFamily="JetBrains Mono">
            {v.toFixed(v < 10 ? 5 : 3)}
          </text>
        ))}

        {/* candles */}
        {candles.map((c, i) => {
          const up = c.close >= c.open;
          const color = up ? "#2DD4BF" : "#FB7185";
          const x = xOf(i);
          const yH = yOf(c.high), yL = yOf(c.low);
          const yO = yOf(c.open), yC = yOf(c.close);
          const yTop = Math.min(yO, yC);
          const bodyH = Math.max(1, Math.abs(yC - yO));
          // Pattern highlight stripes
          const isA = i === aIdx, isB = i === bIdx, isC = i === cIdx;
          return (
            <g key={i}>
              {(isA || isB || isC) && (
                <rect x={x - cw / 2} y={padY} width={cw} height={H - padY * 2}
                      fill={isC ? "#2DD4BF0A" : "#FAFAFA08"} />
              )}
              <line x1={x} y1={yH} x2={x} y2={yL} stroke={color} strokeWidth="1" />
              <rect x={x - bodyW / 2} y={yTop} width={bodyW} height={bodyH}
                    fill={color} opacity={up ? 0.9 : 0.9} />
            </g>
          );
        })}

        {/* signal line if active and matches */}
        {signal && signal.entry_price && (
          <g>
            <line
              x1={padX} x2={W - padX}
              y1={yOf(signal.entry_price)} y2={yOf(signal.entry_price)}
              stroke={signal.side === "BUY" ? "#2DD4BF" : "#FB7185"}
              strokeDasharray="3 3" strokeWidth="1" opacity="0.6"
            />
            <text x={padX + 6} y={yOf(signal.entry_price) - 4} fontSize="10"
                  fill={signal.side === "BUY" ? "#2DD4BF" : "#FB7185"} fontFamily="JetBrains Mono">
              {signal.side} {signal.entry_price.toFixed(5)}
            </text>
          </g>
        )}

        {/* A/B/C labels */}
        <text x={xOf(aIdx)} y={H - 4} fontSize="9" textAnchor="middle" fill="#71717A" fontFamily="JetBrains Mono">A</text>
        <text x={xOf(bIdx)} y={H - 4} fontSize="9" textAnchor="middle" fill="#71717A" fontFamily="JetBrains Mono">B</text>
        <text x={xOf(cIdx)} y={H - 4} fontSize="9" textAnchor="middle" fill="#2DD4BF" fontFamily="JetBrains Mono">C</text>
      </svg>
    </div>
  );
}
